import api from "@/utils/axios";
import unauthenticatedApi from "@/utils/unauthenticatedAxios";
import { API_BASE_URL } from "./apiBase";
import { formatRegionLabel, type RegionLabelLocale } from "@/utils/regionLabel";

/**
 * Region option for dropdowns.
 */
export interface RegionOption {
  id: string;
  name: string;
  slug: string;
  nameEn?: string | null;
}

export interface RegionCatalogRow {
  id: string;
  regionKey: string;
  layerId: string;
  type: "STATE" | "ADMINISTRATIVE_DISTRICT" | "DISTRICT" | "POSTAL_CODE";
  stateSlug: string | null;
  regionSlug: string;
  stateName: string;
  stateNameEn?: string | null;
  regionName: string;
  regionNameEn?: string | null;
  pathNames: string[];
  pathNamesEn?: string[] | null;
}

export interface RegionCatalogLabelEntry extends RegionCatalogRow {
  displayLabel: string;
}

const buildCatalogLabel = (
  row: RegionCatalogRow,
  locale?: RegionLabelLocale | string | null
) => {
  const stateLabel = formatRegionLabel(row.stateName, row.stateNameEn ?? null, locale);
  const regionLabel = formatRegionLabel(row.regionName, row.regionNameEn ?? null, locale);
  return row.layerId === "federalStates"
    ? stateLabel
    : `${regionLabel} (${stateLabel})`;
};

/**
 * Gets all state region options.
 *
 * @returns list of state region options
 */
export async function getRegionStates(): Promise<RegionOption[]> {
  const res = await api.get(`${API_BASE_URL}/api/regions/states`, {
    headers: { Accept: "application/json" },
    validateStatus: () => true,
  });

  if (res.status < 200 || res.status >= 300) {
    throw new Error(`Load regions failed with status ${res.status}`);
  }

  return Array.isArray(res.data) ? (res.data as RegionOption[]) : [];
}

export async function getPublicRegionCatalog(params?: {
  layers?: string[];
  includePostal?: boolean;
  language?: RegionLabelLocale | string | null;
}): Promise<RegionCatalogLabelEntry[]> {
  const query = new URLSearchParams();
  const layers = (params?.layers ?? [])
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  if (layers.length > 0) {
    query.set("layers", layers.join(","));
  }
  if (typeof params?.includePostal === "boolean") {
    query.set("includePostal", String(params.includePostal));
  }

  const queryString = query.toString();
  const url = `${API_BASE_URL}/api/public/regions/catalog${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await unauthenticatedApi.get<RegionCatalogRow[]>(url, {
    headers: { Accept: "application/json" },
    validateStatus: () => true,
  });

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Load public region catalog failed with status ${response.status}`);
  }

  const rows = Array.isArray(response.data) ? response.data : [];
  return rows.map((row) => {
    return {
      ...row,
      displayLabel: buildCatalogLabel(row, params?.language),
    };
  });
}
